import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Input from '@material-ui/core/Input';
import Popper from '@material-ui/core/Popper';
import Tooltip, { InfoPropTypes } from 'components/Tooltip';
import { Checkbox, Box } from '@material-ui/core';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import PopUp from 'components/Operational/PopUp/PopUp';
import { GEOGRAPHICAL_MAP } from 'utils/constants/Map';
import { SITE_FORMIK_KEYS } from 'utils/constants/sites';
import { popupText } from 'utils/constants/popupText';
import { SCHEDULE_PATTERNS } from 'utils/constants/schedule';
import { useTranslation } from 'react-i18next';
import Label from 'components/Label';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const WithBackground = withStyles(theme => ({
  root: {
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
}))(Box);

const useStyles = makeStyles(theme => ({
  formControl: {
    display: 'flex',
    flexWrap: 'nowrap',
  },
  autocomplete: {
    border: `1px solid ${theme.palette.text.secondary}`,
    '&[aria-expanded="true"] [class*="MuiInputBase-inputAdornedStart"]': {
      padddingRight: '15px !important',
    },
    color: ({ disabled }) => {
      if (typeof disabled === 'boolean' && disabled) {
        return theme.palette.text.disabled;
      } else {
        return theme.palette.text.primary;
      }
    },
    '& [class*="MuiInput-root"]': {
      // width: '100%',
      flexWrap: 'unset',
      height: 30,
      color: ({ disabled }) => {
        if (typeof disabled === 'boolean' && disabled) {
          return theme.palette.secondary.light;
        } else {
          return theme.palette.text.primary;
        }
      },
      background: ({ disabled }) => {
        if (typeof disabled === 'boolean' && disabled) {
          return theme.palette.text.disabled;
        } else {
          return 'none';
        }
      },
    },

    '& [class*="MuiChip-sizeSmall"]': {
      margin: '0px',
    },
  },
  rootInput: {
    color: ({ disabled }) => {
      if (typeof disabled === 'boolean' && disabled) {
        return theme.palette.secondary.light;
      } else {
        return theme.palette.text.primary;
      }
    },
    background: disabled => {
      if (typeof disabled === 'boolean' && disabled) {
        return theme.palette.text.disabled;
      } else {
        return 'none';
      }
    },
    '&::placeholder': ({ disabled }) => {
      if (typeof disabled === 'boolean' && disabled) {
        return theme.palette.secondary.light;
      } else {
        return theme.palette.text.primary;
      }
    },
  },
  option: {
    padding: 0,
    '&:hover': {
      background: theme.palette.primary.main,
      color: theme.palette.secondary.light,
    },
    color: disabled => {
      if (typeof disabled === 'boolean' && disabled) {
        return theme.palette.secondary.light;
      } else {
        return theme.palette.text.primary;
      }
    },
    background: ({ disabled }) => {
      if (typeof disabled === 'boolean' && disabled) {
        return theme.palette.text.disabled;
      } else {
        return 'none';
      }
    },
  },
  menuItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '6px 16px',
    width: '100%',
    '&:hover': {
      background: theme.palette.primary.main,
      color: theme.palette.secondary.light,
    },
  },
  hidden: {
    display: 'none',
  },
  checkbox: {
    marginRight: '8px',
  },
  startAdornment: {
    maxHeight: '60px',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    display: 'flex',
  },
  clearIndicator: {
    color: theme.palette.text.primary,
  },
  popupIndicatorOpen: {
    color: theme.palette.text.primary,
  },
  popupIndicator: {
    color: theme.palette.text.primary,
  },
  InputLabel: {
    display: 'flex',
  },
  popper: {
    border: `0.5px solid ${theme.palette.text.secondary}`,
  },
}));

const Select = ({
  disabled = false,
  required,
  label,
  value = [],
  onChange,
  name,
  options = [],
  error,
  className,
  onFocus,
  onInputChange,
  valueType,
  shrinkLabel = true,
  optionLabelKey = 'name',
  placeholder = 'Select',
  useSelectedValue = false,
  info,
  displayError = true,
  noOptionsText,
  hidden,
  useSameValue,
  multiple,
  groupBy,
  limitTags = 1,
  renderGroup,
  freeSolo = false,
  autoSelect = false,
  showPopup,
  isMapTouched,
  formProps,
  isSurfaceTouched,
  handleSelectChange,
  fromFilters = false,
}) => {
  const { t } = useTranslation();
  const [displayPopup, setDisplayPopup] = useState({});
  const classes = useStyles();

  const SelectPopper = props => {
    return (
      <Popper
        modifiers={{
          flip: {
            enabled: true,
          },
        }}
        placement="bottom-start"
        {...props}
      />
    );
  };

  const handleChange = (e, selectedValue, reason) => {
    e.persist();
    if (
      typeof handleSelectChange === 'function' &&
      name === SITE_FORMIK_KEYS.site
    ) {
      handleSelectChange({
        e,
        handleChange: formProps.handleChange,
        value: GEOGRAPHICAL_MAP,
        name: SITE_FORMIK_KEYS.siteSurfaceImages,
      });
    }
    if (typeof handleSelectChange === 'function' && name === 'scheduleType') {
      if (reason === 'clear') {
        handleSelectChange({
          e,
          handleChange: formProps.handleChange,
          props: formProps,
          value: SCHEDULE_PATTERNS.onceOff,
          name: 'scheduleType',
        });
        return;
      } else {
        handleSelectChange({
          props: formProps,
          value: selectedValue,
          name: 'schedule.activeTab',
        });
      }
    }
    if (
      typeof handleSelectChange === 'function' &&
      reason === 'clear' &&
      name === SITE_FORMIK_KEYS.siteSurfaceImages
    ) {
      handleSelectChange({
        e,
        handleChange: formProps.handleChange,
        value: GEOGRAPHICAL_MAP,
        name: SITE_FORMIK_KEYS.siteSurfaceImages,
      });
      // this is required as we want to set GEOGRAPHICAL_MAP as default value and not null
      return;
    }

    if (multiple) {
      onChange(selectedValue, name, reason);
      return;
    }
    //Cant modify event.target.value its always 0
    //https://stackoverflow.com/questions/68495486/cant-modify-event-target-value-its-always-0
    // seems to be a problem with mui and event propagation
    if (fromFilters) {
      e.target.selectedValue = selectedValue
        ? valueType === 'object'
          ? selectedValue
          : selectedValue.id
        : '';
    } else {
      e.target.value = selectedValue
        ? valueType === 'object'
          ? selectedValue
          : selectedValue.id
        : '';
    }
    /// this sets the form value including null

    e.target.name = name;
    e.persist();
    onChange(useSelectedValue ? selectedValue : e);
  };

  const handleChangeWithPopup = (e, selectedValue, reason) => {
    // propagate the event
    e.persist();
    if (
      name === SITE_FORMIK_KEYS.site &&
      formProps?.values?.siteSurfaceImages?.id === GEOGRAPHICAL_MAP.id
    ) {
      handleChange(e, selectedValue, reason);
    } else if (
      name === SITE_FORMIK_KEYS.siteSurfaceImages &&
      (formProps?.values?.checkpoints?.length > 0 ||
        formProps?.values?.location?.length > 0)
    ) {
      setDisplayPopup({ e, selectedValue, reason });
    } else if (
      name === SITE_FORMIK_KEYS.site &&
      (formProps?.values?.checkpoints?.length > 0 ||
        formProps?.values?.location?.length > 0)
    ) {
      setDisplayPopup({ e, selectedValue, reason });
    } else {
      handleChange(e, selectedValue, reason);
    }
  };

  const getOptionId = option =>
    typeof option === 'string' ? option : option.id;

  const getOptionName = option => {
    return typeof option === 'string'
      ? option
      : option?.displayName
      ? t(option.displayName)
      : t(option[optionLabelKey]) || '';
  };

  const getValue = () => {
    if (multiple) {
      return value === '' ? [] : value;
    }
    if (value) {
      return typeof value === 'string' || Number.isInteger(value)
        ? options.find(item => item.id === value) || null
        : Array.isArray(value)
        ? null
        : value;
    }

    return null;
  };

  const handleOnChange = (e, selectedValue, reason) => {
    // valid for a selected field which has showPopup key set as true
    e.persist();
    if (showPopup) {
      return handleChangeWithPopup(e, selectedValue, reason);
    } else {
      return handleChange(e, selectedValue, reason);
    }
  };

  const textErrorOrBool = typeof error === 'string' ? t(error) : error;

  return (
    <>
      <FormControl
        disabled={disabled}
        className={cn(classes.formControl, className, {
          [classes.hidden]: hidden,
        })}
        required={required}
        error={!disabled && textErrorOrBool}
      >
        <InputLabel
          shrink={shrinkLabel}
          htmlFor={name}
          className={disabled ? classes.disabled : classes.input}
          classes={{ root: classes.InputLabel }}
          focused={false}
        >
          {t(label)}
          {info && <Tooltip text={info} />}
        </InputLabel>
        <Autocomplete
          freeSolo={freeSolo} // adds ability to use autocomplete as normal input
          autoSelect={autoSelect} // selects the option if moved away from input, only applicable in google address finder
          multiple={multiple}
          value={useSameValue ? value : getValue()}
          classes={{
            root: classes.autocomplete,
            option: classes.option,
            popupIndicator: classes.popupIndicator,
            clearIndicator: classes.clearIndicator,
            popupIndicatorOpen: classes.popupIndicatorOpen,
            popper: classes.popper,
            input: classes.rootInput,
          }}
          blurOnSelect={!multiple}
          disableCloseOnSelect
          limitTags={limitTags}
          onOpen={onFocus}
          onChange={handleOnChange}
          onInputChange={onInputChange}
          disabled={disabled}
          options={options}
          // popupIcon={<ArrowDownIcon />}
          PopperComponent={SelectPopper}
          renderInput={params => {
            return (
              <Input
                placeholder={t(placeholder)}
                disableUnderline
                {...params.InputProps}
                startAdornment={
                  <div className={classes.startAdornment}>
                    {params.InputProps.startAdornment}
                  </div>
                }
                endAdornment={
                  <>
                    {false ? 'l' : null}
                    {params.InputProps.endAdornment}
                  </>
                }
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'new-password',
                  form: {
                    autoComplete: 'off',
                  },
                }}
              />
            );
          }}
          //This helps to get the option name when you select from the list
          getOptionLabel={option => {
            if (
              showPopup &&
              (isMapTouched || isSurfaceTouched) &&
              formProps.values.siteSurfaceImages &&
              name === SITE_FORMIK_KEYS.siteSurfaceImages
            ) {
              // show the last selected option string
              return formProps.values.siteSurfaceImages[optionLabelKey];
            } else if (
              showPopup &&
              (isMapTouched || isSurfaceTouched) &&
              name === SITE_FORMIK_KEYS.site &&
              formProps.values.site
            ) {
              // select the last selected site string
              const id =
                typeof formProps.values.site === 'object'
                  ? formProps.values.site.id
                  : formProps.values.site;
              const siteName = options.filter(f => f.id === id);
              if (siteName && siteName.length > 0) {
                return siteName[0][optionLabelKey];
              }
              return '';
            } else {
              return getOptionName(option);
            }
          }}
          getOptionSelected={(option, selected) =>
            selected ? option.id === getOptionId(selected) : false
          }
          renderOption={(option, { selected }) => {
            return multiple ? (
              <>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  className={classes.checkbox}
                  checked={selected}
                />
                <Label
                  icon={option?.icon ? option.icon : ''}
                  alt={'icons'}
                  className={classes.icon}
                />
                {t(option[optionLabelKey])}
              </>
            ) : (
              <WithBackground
                className={classes.menuItem}
                name={t(name)}
                id={name}
                value={getOptionId(option)}
              >
                <Label
                  icon={option?.icon ? option.icon : ''}
                  alt={'icons'}
                  className={classes.icon}
                />
                {getOptionName(option)}
                {/* This is how options are displayed inside the select list */}
              </WithBackground>
            );
          }} // If you provide a translated text then it will show the text other wise library will convert text according to the selected language
          noOptionsText={noOptionsText ? t(noOptionsText) : undefined}
          ChipProps={{
            size: 'small',
          }}
          {...(groupBy && { groupBy })}
          {...(renderGroup && { renderGroup })}
        />
        {displayError && textErrorOrBool && (
          <FormHelperText id="component-error-text">
            {textErrorOrBool}
          </FormHelperText>
        )}
      </FormControl>
      {Object.keys(displayPopup).length > 0 && (
        <PopUp
          title={
            name !== SITE_FORMIK_KEYS.siteSurfaceImages
              ? popupText.globalMessage.popupTitle
              : popupText.siteSurfaceMessage.popupTitle
          }
          close={() => setDisplayPopup({})}
          statusMessage={
            name !== SITE_FORMIK_KEYS.siteSurfaceImages
              ? popupText.globalMessage.popupStatusMessage
              : popupText.siteSurfaceMessage.popupStatusMessage
          }
          actionMessage={
            name !== SITE_FORMIK_KEYS.siteSurfaceImages
              ? popupText.globalMessage.popupActionMessage
              : popupText.siteSurfaceMessage.popupActionMessage
          }
          submit={() => {
            // delete the checkpoints
            if (formProps?.values?.checkpoints) {
              formProps.values.checkpoints = [];
            }
            if (formProps.values?.location) {
              formProps.values.location = [];
            }
            if (formProps.values?.additionalMarkers) {
              formProps.values.additionalMarkers = [];
            }

            // change the field Name
            handleChange(
              displayPopup.e,
              displayPopup.selectedValue,
              displayPopup.reason,
            );
            // close the popup
            setDisplayPopup({});
          }}
          submitBtnText={popupText.submitBtnText}
          cancel={() => {
            // close the popup
            setDisplayPopup({});
          }}
          cancelBtnText={popupText.cancelBtnText}
        />
      )}
    </>
  );
};

Select.propTypes = {
  disabled: PropTypes.bool,
  className: PropTypes.string,
  autoComplete: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
    PropTypes.object,
  ]),
  onInputChange: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  name: PropTypes.string.isRequired,
  options: PropTypes.array,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  valueType: PropTypes.string,
  shrinkLabel: PropTypes.bool,
  optionLabelKey: PropTypes.string,
  useSelectedValue: PropTypes.bool,
  placeholder: PropTypes.string,
  info: InfoPropTypes,
  displayError: PropTypes.bool,
  noOptionsText: PropTypes.string,
  hidden: PropTypes.bool,
  useSameValue: PropTypes.bool,
  multiple: PropTypes.bool,
  groupBy: PropTypes.func,
  limitTags: PropTypes.number,
  renderGroup: PropTypes.func,
  handleSelectChange: PropTypes.func,
  freeSolo: PropTypes.bool,
  autoSelect: PropTypes.bool,
  showPopup: PropTypes.bool,
  isMapTouched: PropTypes.bool,
  isSurfaceTouched: PropTypes.bool,
  formProps: PropTypes.object,
  fromFilters: PropTypes.bool,
};

Select.defaultProps = {
  disabled: false,
};

export default Select;
